<template>
  <div>
    <ListWrapper
      ref="listWrapper"
      :url="url"
      :addButtonText="$lang.new_term"
      @getPermissions="getPermissions"
      @handleAddButton="handleOpenAddTermPin"
    >
      <template slot-scope="{ items }">
        <CardList
          :items="reGenerateItems(items)"
          :deletedItemsId="deletedItemsId"
          loadingComponent="Term"
          :showBody="showBody"
        >
          <template slot="header" slot-scope="{ item }">
            <b-row class="width-percent-100 margin-0">
              <b-col
                cols="12"
                class="padding-x-0 padding-y-8 flex-between tablet-none desktop-none"
              >
                <span :class="`chips chips--${generateStatusVariant(item)}`">
                  {{ pathOr("", ["status", "caption"], item) }}
                </span>
                <MenuPopupWrapper
                  iconClass="margin-right-8 color-complementary-200 cursor--pointer"
                  menuClass="left-0 top-10 z-index-1 padding-top-24"
                  :items="itemButtons"
                  :permissions="permissions"
                  :showMenu="itemMenuId === item.code"
                  :rowItem="item"
                  :hasEventCapturing="false"
                  @click="showItemMenu"
                  @close="closeItemMenu"
                />
              </b-col>
              <b-col
                cols="12"
                sm="4"
                md="3"
                class="padding-0 flex-start-center flex-direction-column mobile-margin-bottom-8"
              >
                <div
                  class="font-size-14 color-complementary-400 margin-bottom-8"
                >
                  {{ pathOr("", ["caption"], item) }}
                </div>
                <div class="font-size-12 color-complementary-300">
                  {{ pathOr("", ["company", "caption"], item) }}
                </div>
              </b-col>
              <b-col
                cols="12"
                sm="2"
                md="3"
                class="padding-0 font-size-12 color-complementary-300 flex-center mobile-none"
              >
                {{ `${$lang.term_number}: ${pathOr("", ["id"], item)}` }}
              </b-col>
              <b-col
                cols="12"
                class="padding-0 font-size-12 color-complementary-300 tablet-none desktop-none"
              >
                {{ `${$lang.term_number}: ${pathOr("", ["id"], item)}` }}
              </b-col>
              <b-col
                cols="12"
                sm="3"
                class="padding-0 mobile-none position-relative text-right"
              >
                <TermChart
                  :series="item.chartSeries"
                  :infoBoxData="generateTermChartData(item)"
                  :code="item.code"
                  :chartItemInfoId="chartItemInfoId"
                  @handleShowInfo="showChartItemInfo"
                />
              </b-col>
              <b-col
                cols="12"
                class="padding-0 margin-top-8 tablet-none desktop-none"
              >
                <TermChart
                  :series="item.chartSeries"
                  :infoBoxData="generateTermChartData(item)"
                  :code="item.code"
                  :chartItemInfoId="chartItemInfoId"
                  chartBoxClass="flex-start"
                  infoBoxClass="right-80 top-0"
                  @handleShowInfo="showChartItemInfo"
                />
              </b-col>
              <b-col cols="12" sm="3" class="padding-0 flex-end mobile-none">
                <span :class="`chips chips--${generateStatusVariant(item)}`">
                  {{ pathOr("", ["status", "caption"], item) }}
                </span>
                <MenuPopupWrapper
                  iconClass="margin-right-8 color-complementary-200 cursor--pointer"
                  menuClass="left-0 top-10 z-index-1 padding-top-24"
                  :items="itemButtons"
                  :permissions="permissions"
                  :showMenu="itemMenuId === item.code"
                  :rowItem="item"
                  :hasEventCapturing="false"
                  @click="showItemMenu"
                  @close="closeItemMenu"
                />
              </b-col>
            </b-row>
          </template>
          <template slot="body">
            <b-row class="m-0" v-if="bodyData">
              <b-col cols="12" class="padding-0">
                <span class="">
                  {{ $lang.start }}: <span v-html="bodyData.startDate" />
                </span>
                <span class="margin-left-16">
                  {{ $lang.end }}: <span v-html="bodyData.endDate" />
                </span>
              </b-col>
              <b-col v-if="bodyData.license" cols="12" class="padding-0">
                {{ $lang.license_type }}: {{ bodyData.license }}
              </b-col>
              <b-col v-if="bodyData.cooperation" cols="12" class="padding-0">
                {{ $lang.cooperation_type }}: {{ bodyData.cooperation }}
              </b-col>
              <b-col cols="12" class="padding-0">
                {{ $lang.show_register_link }}: {{ bodyData.linkShow }}
              </b-col>
              <b-col
                v-if="bodyData.preRegisterCode"
                cols="12"
                class="padding-0"
              >
                {{ $lang.termRegistrationLinkAddress }}:
                <a
                  :href="registrationUrl(bodyData.preRegisterCode)"
                  target="_blank"
                >
                  {{ registrationUrl(bodyData.preRegisterCode) }}
                </a>
              </b-col>
              <b-col v-if="bodyData.levelsCount" cols="12" class="padding-0">
                {{ $lang.levels }}: {{ bodyData.levelsCount }}
              </b-col>
              <b-col cols="12" class="padding-0">
                <span class="margin-left-8">
                  {{ $lang.created_by }}: {{ bodyData.createdBy }}
                </span>
                <span class="">
                  {{ $lang.created_at }}:
                  <span v-html="bodyData.createdAt" />
                </span>
              </b-col>
            </b-row>
          </template>
        </CardList>
      </template>
    </ListWrapper>
    <Modal
      :show="removeTermConfirm"
      :title="$lang.remove_term"
      :closeOnBackdrop="false"
      :hasClose="false"
      :okButton="true"
      :okButtonText="$lang.remove"
      okButtonVariant="filled-error"
      size="sm"
      :okLoading="removeTermLoading"
      :ok="removeTermItem"
      @close="closeRemoveTermConfirm"
    >
      <template #body>
        <div class="margin-bottom-24 font-size-14 color-complementary-300">
          {{ $lang.remove_sure_message.replace("{0}", $lang.term) }}
        </div>
      </template>
    </Modal>
    <AddTermPin
      :visible="isVisibleAddTermPin"
      :permissions="permissions"
      @close="handleCloseAddTermPin"
      @fetchData="listWrapperReload"
    />
  </div>
</template>

<script>
import inputMixin from "@/mixin/inputMixin.vue";
import ListWrapper from "@/components/re-design/lists/ListWrapper.vue";
import CardList from "@/components/re-design/lists/CardList.vue";
import MenuPopupWrapper from "@/components/re-design/modal/MenuPopupWrapper.vue";
import Modal from "@/components/re-design/modal/Modal.vue";
import AddTermPin from "./components/AddTermPin.vue";
import TermChart from "./components/TermChart.vue";
import { required } from "vuelidate/lib/validators";
import { pathOr } from "ramda";
import { generateApiUrl, goToRoute } from "@/shared/utils";
import { mapActions, mapGetters } from "vuex";
import { getTermV2, removeTerm } from "@/modules/profile/services";

export default {
  mixins: [inputMixin],
  components: {
    ListWrapper,
    CardList,
    MenuPopupWrapper,
    Modal,
    AddTermPin,
    TermChart,
  },
  validations() {
    return {
      form: {},
    };
  },
  data() {
    return {
      bodyDataCached: [],
      bodyData: null,
      permissions: [],
      itemMenuId: 0,
      termSelected: null,
      form: {},
      currentItemId: null,
      removeTermConfirm: false,
      removeTermLoading: false,
      deletedItemsId: [],
      isVisibleAddTermPin: false,
      chartItemInfoId: 0,
    };
  },
  computed: {
    ...mapGetters(["userInfo"]),
    url() {
      return generateApiUrl(["profile", "term"]);
    },
    itemButtons() {
      return [
        {
          caption: this.$lang.edit,
          icon: "pencil-outline",
          show: true,
          permissions: ["admin", "edit"],
          loading: false,
          click: this.goToEditTerm,
        },
        {
          caption: this.$lang.remove,
          icon: "trash-can-outline",
          show: true,
          permissions: ["admin", "edit"],
          loading: false,
          click: this.showRemoveTermConfirm,
        },
      ];
    },
  },
  methods: {
    ...mapActions(["setAction"]),
    pathOr,
    listWrapperReload() {
      this.$refs.listWrapper.reload();
    },
    generateFullName(item) {
      const name = pathOr("", ["person", "name"], item);
      const family = pathOr("", ["person", "family"], item);
      return `${name} ${family}`;
    },
    async showBody(item) {
      try {
        const code = pathOr(null, ["code"], item);
        // check exist in cache data
        const cacheData = this.bodyDataCached.find(
          (cacheItem) => cacheItem.code === code
        );
        if (cacheData) {
          this.bodyData = cacheData;
          return;
        }

        const response = await getTermV2(code);
        this.bodyData = response;
        this.bodyDataCached = [...this.bodyDataCached, response];
      } catch (error) {
        this.$generateError(error);
      }
    },
    getPermissions(value) {
      this.permissions = value;
    },
    showItemMenu(item) {
      const code = pathOr(null, ["code"], item);
      this.itemMenuId = this.itemMenuId === code ? null : code;
    },
    closeItemMenu() {
      this.itemMenuId = null;
    },
    setMobileHeaderData(item) {
      const caption = pathOr("", ["caption"], item);
      const title = `${this.$lang.edit_term} <span class="margin-right-4">${caption}</span>`;
      this.setAction({
        key: "mobileHeaderTitleText",
        value: title,
      });
      this.setAction({
        key: "mobileHeaderBaseRoute",
        value: this.$route.name,
      });
    },
    goToEditTerm(item) {
      this.setMobileHeaderData(item);
      const code = pathOr(null, ["code"], item);
      goToRoute("edit-term", { code }, {}, false);
    },
    generateStatusVariant(item) {
      const key = pathOr(null, ["status", "id"], item);
      const variants = {
        1: "warning",
        2: "success",
        3: "complementary",
      };
      return pathOr("", [key], variants);
    },
    reGenerateItems(items) {
      return items.map((item) => {
        const activeParvandehCount = pathOr(
          0,
          ["active_parvandeh_count"],
          item
        );
        const endParvandehCount = pathOr(0, ["end_parvandeh_count"], item);
        const preRegisterCount = pathOr(0, ["pre_register_count"], item);

        const totalChartData =
          activeParvandehCount + endParvandehCount + preRegisterCount;

        const activeParvandehPercent =
          (activeParvandehCount * 100) / totalChartData || 0;
        const endParvandehPercent =
          (endParvandehCount * 100) / totalChartData || 0;
        const preRegisterPercent =
          (preRegisterCount * 100) / totalChartData || 0;

        const disabled = pathOr(null, ["status", "id"], item) === 3;

        return {
          ...item,
          chartSeries: [
            Math.round(activeParvandehPercent),
            Math.round(endParvandehPercent),
            Math.round(preRegisterPercent),
          ],
          disabled,
          deleted: false,
        };
      });
    },
    showRemoveTermConfirm(item) {
      this.currentItemId = pathOr(null, ["code"], item);
      this.removeTermConfirm = true;
    },
    closeRemoveTermConfirm() {
      this.currentItemId = null;
      this.removeTermConfirm = false;
    },
    async removeTermItem() {
      try {
        this.removeTermLoading = true;
        const response = await removeTerm(this.currentItemId);
        const { message } = response;
        this.$notify.success(message);
        this.deletedItemsId.push(this.currentItemId);
        this.closeRemoveTermConfirm();
        this.listWrapperReload();
      } catch (error) {
        this.$generateError(error);
      } finally {
        this.removeTermLoading = false;
      }
    },
    handleOpenAddTermPin() {
      this.isVisibleAddTermPin = true;
    },
    handleCloseAddTermPin() {
      this.isVisibleAddTermPin = false;
    },
    registrationUrl(code) {
      const routerData = this.$router.resolve({
        name: "detail-registrations",
        params: { code },
      });
      return window.location.origin + routerData.href;
    },
    showChartItemInfo(code) {
      this.chartItemInfoId = code;
    },
    generateTermChartData(item) {
      return {
        activeParvandehCount: pathOr(0, ["active_parvandeh_count"], item),
        archiveParvandehCount: pathOr(0, ["end_parvandeh_count"], item),
        successPreRegisterCount: pathOr(0, ["pre_register_count"], item),
      };
    },
  },
};
</script>
